import { useFirebase } from '@/firebase';
import { useUserProfile } from '@/firebase/hooks/use-user-profile';
import type { NarrativeSummary, Novel } from '@/lib/types';
import { useEffect, useMemo, useState } from 'react';
import { useUserNovels } from '@/context/user-novels-context';

/**
 * Hook to retrieve and subscribe to all novels readable by the current user
 * (owned, collaborated, or reader access) containing at least one preview/public chapter.
 */
export function useReadableNovels() {
  const { backend, user } = useFirebase();
  const { userData } = useUserProfile();
  const collaboratedNovels = userData.collaboratedNovels || [];

  const { ownedNovels: ownedNovelsList, isLoading } = useUserNovels();
  const [novelsData, setNovelsData] = useState<Record<string, Novel>>({});

  // 2. Combine all access points
  const allAccessList = useMemo(() => {
    const list: { ownerId: string; novelId: string }[] = [];
    ownedNovelsList.forEach((n) => {
      list.push({ ownerId: n.ownerId || user?.uid || '', novelId: n.id });
    });
    collaboratedNovels.forEach((collab) => {
      list.push({ ownerId: collab.ownerId, novelId: collab.novelId });
    });
    return list;
  }, [ownedNovelsList, collaboratedNovels, user]);

  // 3. Subscribe to full resolved novel for each access point
  useEffect(() => {
    if (allAccessList.length === 0) {
      setNovelsData({});
      return;
    }

    const unsubscribes = allAccessList.map((access) => {
      return backend.novels.subscribeNovel(
        access.ownerId,
        access.novelId,
        (novel) => {
          if (novel) {
            setNovelsData((prev) => ({
              ...prev,
              [`${access.ownerId};${access.novelId}`]: novel,
            }));
          }
        },
        (error) => {
          console.error('Failed to listen to novel:', error);
        }
      );
    });

    return () => unsubscribes.forEach((unsub) => unsub());
  }, [backend, allAccessList]);

  // Combine and format the novels list
  const allNovels = useMemo(() => {
    const list: Novel[] = [];
    allAccessList.forEach((access) => {
      const key = `${access.ownerId};${access.novelId}`;
      const novel = novelsData[key];
      if (novel) {
        list.push({
          ...novel,
          id: key, // Store composite ID
          ownerId: novel.ownerId || access.ownerId,
        });
      }
    });
    return list;
  }, [allAccessList, novelsData]);

  // Filter novels containing at least one preview/public chapter
  const readableNovels = useMemo(() => {
    return allNovels.filter((novel) => {
      const embeddedChapters = novel.summaries?.['chapters'];
      const chapters = embeddedChapters ? (Object.values(embeddedChapters) as NarrativeSummary[]) : [];

      // Check if there is at least one chapter ready for readers
      return chapters.some(
        (ch) => ch.publishStatus === 'preview' || ch.publishStatus === 'public'
      );
    });
  }, [allNovels]);

  return {
    readableNovels,
    isLoading,
  };
}
